import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { authApi } from "../../services/authApi";
import { invitationApi } from "../../services/invitationApi";
import { employeeApi } from "../../services/employeeApi";
import { formatApiErrorDetail } from "../../lib/api";
import { ROLES, MANAGEMENT_ROLES } from "../../lib/constants";
import { useLocalization } from "../../hooks/useLocalization";
import { PageHelpBanner } from "../../components/PageHelpBanner";

const CREATABLE_ROLES = MANAGEMENT_ROLES.filter((r) => r !== ROLES.SYSTEM_OWNER);
const EMPTY_FORM = {
  legal_name: "", display_name: "", email: "", role: ROLES.ONBOARDING_MANAGER,
  employee_id: "", location_scope: "", department_scope: "", account_expires_at: "", notes: "",
};

export default function ManagementUserCreatePage() {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);

  useEffect(() => {
    employeeApi.list().then((r) => setEmployees(r.data.filter((emp) => !emp.user_id)));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        legal_name: form.legal_name,
        display_name: form.display_name || undefined,
        email: form.email,
        role: form.role,
        employee_id: form.employee_id || undefined,
        location_scope: form.location_scope ? form.location_scope.split(",").map((s) => s.trim()).filter(Boolean) : undefined,
        department_scope: form.department_scope ? form.department_scope.split(",").map((s) => s.trim()).filter(Boolean) : undefined,
        account_expires_at: form.account_expires_at || undefined,
        notes: form.notes || undefined,
      };
      const { data } = await authApi.createManagementUser(payload);
      setCreated(data);
      toast.success(t("invitations.managementAccountCreated"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleCopy = (invitationId, link) => {
    navigator.clipboard.writeText(link);
    invitationApi.markCopied(invitationId).catch(() => {});
    toast.success(t("invitations.linkCopied"));
  };

  return (
    <div className="max-w-xl" data-testid="management-user-create-page">
      <PageHelpBanner pageKey="management-user-create" title="How to use this page">
        <p>Creates a management-console login (Access Profile), not an employee master record - use New Employee for that. Pick a role, optionally link to an existing employee, then copy the activation link and send it to them directly (no email is sent automatically in preview).</p>
      </PageHelpBanner>
      <button
        onClick={() => navigate("/management/security/users")}
        data-testid="back-to-directory-link"
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft size={15} /> {t("invitations.backToDirectory")}
      </button>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-6">{t("invitations.createManagementAccount")}</h2>

      {created ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="management-user-created-panel">
          <p className="text-sm font-semibold text-amber-900 mb-2">{t("invitations.managementAccountCreated")}</p>
          <p className="text-xs text-amber-700 mb-2">{t("invitations.previewLinkNote")}</p>
          <div className="flex items-center justify-between gap-3 bg-white rounded-lg p-3 border border-amber-200">
            <p className="text-xs text-amber-900 truncate font-mono" data-testid="management-user-activation-link">{created.activation_link_preview}</p>
            <button
              data-testid="copy-management-activation-link-button"
              onClick={() => handleCopy(created.id, created.activation_link_preview)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 active:scale-95 transition-all shrink-0"
            >
              <Copy size={12} /> {t("invitations.copyLink")}
            </button>
          </div>
          <button
            onClick={() => { setCreated(null); setForm(EMPTY_FORM); }}
            data-testid="create-another-management-user-button"
            className="mt-4 text-xs font-semibold text-blue-600 hover:underline"
          >
            {t("invitations.createManagementAccount")} +
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <input data-testid="mgmt-user-legal-name-input" required placeholder={t("invitations.legalName")} value={form.legal_name} onChange={(e) => setForm((f) => ({ ...f, legal_name: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="mgmt-user-display-name-input" placeholder={t("invitations.displayName")} value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="mgmt-user-email-input" required type="email" placeholder={t("auth.email")} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <select data-testid="mgmt-user-role-select" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm">
            {CREATABLE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select data-testid="mgmt-user-employee-select" value={form.employee_id} onChange={(e) => setForm((f) => ({ ...f, employee_id: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm">
            <option value="">{t("invitations.employeeLink")}</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.employee_id} - {emp.legal_name}</option>)}
          </select>
          <input data-testid="mgmt-user-location-scope-input" placeholder={t("invitations.locationScope")} value={form.location_scope} onChange={(e) => setForm((f) => ({ ...f, location_scope: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="mgmt-user-department-scope-input" placeholder={t("invitations.departmentScope")} value={form.department_scope} onChange={(e) => setForm((f) => ({ ...f, department_scope: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="mgmt-user-expires-input" type="date" placeholder={t("invitations.accountExpiresAt")} value={form.account_expires_at} onChange={(e) => setForm((f) => ({ ...f, account_expires_at: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-500" />
          <textarea data-testid="mgmt-user-notes-input" placeholder={t("invitations.notes")} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" rows={2} />
          {error && <p className="text-sm text-red-500" data-testid="mgmt-user-create-error">{error}</p>}
          <button data-testid="submit-mgmt-user-button" type="submit" className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">
            {t("invitations.createAccount")}
          </button>
        </form>
      )}
    </div>
  );
}
